import * as DesignerActions from './designer.actions';
import * as ValidationActions from '../../../validation/store/validation/validation.actions';
import { Action, createReducer, on } from '@ngrx/store';
import { IDesigner } from '../../models/designer.model';
import { ITaskCell } from '../../models/task.model';
import { ITableVisualizationModel } from 'src/app/spa/models/table-visualization.model';
import { IReferenceDatasetResponse } from 'src/app/spa/models/spa-project.model';

export type State = IDesigner;

const initialState: State = {
  task: null,
  repeat: {
    enabled: false,
    horizontal: {
      repeat: 1,
      gap: 1
    },
    vertical: {
      repeat: 1,
      gap: 1
    }
  },
  clipboard: {
    cell: null,
    logics: null
  },
  loaders: {
    task: false
  }
};

const designerReducer = createReducer(
  initialState,
  on(DesignerActions.ClearState, (): State => initialState),

  on(DesignerActions.SetTask, (state, { task }): State => {
    return { ...state, task };
  }),

  on(DesignerActions.AddTlfs, (state, { tlfs }): State => {
    return { ...state, task: { ...state.task, tlfs } };
  }),

  on(DesignerActions.SetReferenceTlf, (state, { tlf }): State => {
    const references = [{ tlf }];
    return { ...state, task: { ...state.task, issues: [{ references }] } };
  }),

  on(DesignerActions.SetCell, (state, { cell }): State => {
    const issues = state.task?.issues ?? [];
    const firstIssue = issues?.[0] ?? {};
    return { ...state, task: { ...state.task, issues: [{ ...firstIssue, cell }] } };
  }),

  on(DesignerActions.SetReferenceCell, (state, { cell }): State => {
    const issues = state.task?.issues ?? [];
    const firstIssue = issues?.[0] ?? {};
    const references = firstIssue.references?.[0] ? [{ ...firstIssue.references[0], cells: [cell] }] : [{ cells: [cell] }];
    return { ...state, task: { ...state.task, issues: [{ ...firstIssue, references }] } };
  }),

  on(DesignerActions.AddCells, (state, { cells }): State => {
    const issues = state.task?.issues ?? [];
    const firstIssue = issues?.[0] ?? {};
    const firstIssueCells = addCells(issues?.[0]?.cells, cells);
    return { ...state, task: { ...state.task, issues: [{ ...firstIssue, cells: firstIssueCells }] } };
  }),

  on(DesignerActions.SetCells, (state, { cells }): State => {
    const issues = state.task?.issues ?? [];
    const firstIssue = issues?.[0] ?? {};
    return { ...state, task: { ...state.task, issues: [{ ...firstIssue, cells }] } };
  }),

  on(DesignerActions.RemoveCells, (state, { cells }): State => {
    const issues = state.task?.issues ?? [];
    const firstIssue = issues?.[0] ?? {};
    const firstIssueCells = removeCells(issues?.[0]?.cells, cells);
    return { ...state, task: { ...state.task, issues: [{ ...firstIssue, cells: firstIssueCells }] } };
  }),

  on(DesignerActions.SetDescription, (state, { description }): State => {
    return { ...state, task: { ...state.task, description } };
  }),

  on(DesignerActions.ClearCells, (state): State => {
    return { ...state, task: { ...state.task, outcome: null, description: null, issues: [{}] } };
  }),

  on(DesignerActions.SetClipboard, (state, { cell, logics }): State => {
    return { ...state, clipboard: { cell, logics } };
  }),

  on(DesignerActions.ClearClipboard, (state): State => {
    return { ...state, clipboard: { cell: null, logics: null } };
  }),

  on(ValidationActions.CreateTask, (state): State => {
    return { ...state, loaders: { task: true } };
  }),

  on(ValidationActions.CreateTaskSuccess, (state): State => {
    const referenceIssue = state.task.issues.find((issue) => issue?.references?.length);
    const cell = state.task.issues?.[0]?.cell;
    const issues = referenceIssue ? [{ cell, references: referenceIssue.references }] : [{}];
    return { ...state, task: { ...state.task, issues }, repeat: { ...initialState.repeat }, loaders: { task: false } };
  }),

  on(ValidationActions.CreateTaskFailed, (state): State => {
    return { ...state, loaders: { task: false } };
  }),

  on(ValidationActions.UpdateTask, (state): State => {
    return { ...state, loaders: { task: true } };
  }),

  on(ValidationActions.UpdateTaskSuccess, (state): State => {
    return { ...state, loaders: { task: false } };
  }),

  on(ValidationActions.UpdateTaskFailed, (state): State => {
    return { ...state, loaders: { task: false } };
  }),

  on(DesignerActions.GetReferenceTableSuccess, (state, { payload }): State => {
    const tlf = getTlfFromReferenceResponse(payload);
    const cells =
      tlf.name.endsWith('.sas7bdat') || tlf.name.endsWith('.xpt')
        ? [{ col: 0, row: 0, table: { name: tlf.name, index: 0 } }]
        : undefined;
    const references = [{ tlf, cells }];
    return { ...state, task: { ...state.task, issues: [{ references }] } };
  }),

  on(DesignerActions.EnableRepeat, (state): State => {
    return {
      ...state,
      repeat: {
        enabled: true,
        horizontal: {
          repeat: 1,
          gap: 0
        },
        vertical: {
          repeat: 1,
          gap: 0
        }
      }
    };
  }),

  on(DesignerActions.DisableRepeat, (state): State => {
    return { ...state, repeat: { ...state.repeat, enabled: false } };
  }),

  on(DesignerActions.UpdateRepeat, (state, { repeat }): State => {
    return { ...state, repeat: { ...state.repeat, ...repeat } };
  })
);

const addCells = (cells: ITaskCell[], newCells: ITaskCell[]) => {
  const updatedCells = cells ? [...cells] : [];
  for (const cell of newCells) {
    if (updatedCells.some((updated) => isSameCell(updated, cell))) continue;
    updatedCells.push(cell);
  }
  return updatedCells;
};

const removeCells = (cells: ITaskCell[], newCells: ITaskCell[]) => {
  const updatedCells = cells ? [...cells] : [];
  for (const cell of newCells) {
    const index = updatedCells.findIndex((updated) => isSameCell(updated, cell));
    if (index < 0) continue;
    updatedCells.splice(index, 1);
  }
  return updatedCells;
};

const isSameCell = ({ col, row, table }: ITaskCell, cell: ITaskCell) => {
  return col === cell.col && row === cell.row && table.index === cell.table.index && table.name === cell.table.name;
};

export const reducer = (state: State, action: Action): State => designerReducer(state, action);

const getTlfFromReferenceResponse = (response: ITableVisualizationModel[] | IReferenceDatasetResponse) => {
  if (Array.isArray(response)) {
    const [{ fileName: name, tableIndex: index }] = response;
    return { name, index };
  } else {
    const { name } = response;
    const index = 0;
    return { name, index };
  }
};
